import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  IconButton,
  Alert,
  CircularProgress,
} from '@mui/material';
import emailjs from '@emailjs/browser';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LanguageIcon from '@mui/icons-material/Language';
import { useLanguage } from '../contexts/LanguageContext';
import { Reveal, Glow, GridTexture, BRAND_GRAD } from './premium';

const ContactSection: React.FC = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState<{ type: 'success' | 'error' | null; message: string }>({
    type: null,
    message: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setStatus({ type: null, message: '' });

    try {
      // EmailJS configuration
      const serviceId = 'service_0cinpnk';
      const templateId = 'template_16ek089';
      const publicKey = 'tG4tvzouDarcE0Xb_';

      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        message: formData.message,
        to_email: 'contact@onionshack.com',
      };

      await emailjs.send(serviceId, templateId, templateParams, publicKey);
      
      setStatus({
        type: 'success',
        message: t.contact.form.success,
      });
      
      // Reset form
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      setStatus({
        type: 'error',
        message: t.contact.form.error,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: <LocationOnIcon sx={{ color: '#0055B8' }} />,
      title: t.contact.address,
    },
    {
      icon: <PhoneIcon sx={{ color: '#0055B8' }} />,
      title: t.contact.phone,
    },
    {
      icon: <EmailIcon sx={{ color: '#0055B8' }} />,
      title: t.contact.email,
    },
    {
      icon: <LanguageIcon sx={{ color: '#0055B8' }} />,
      title: 'www.onionshack.com',
    },
  ];

  return (
    <Box id="contact" sx={{ position: 'relative', overflow: 'hidden', py: { xs: 9, md: 13 }, backgroundColor: '#FFFFFF' }}>
      <Glow color="#6DABE4" bottom="-12%" left="-8%" size={460} opacity={0.13} />
      <GridTexture opacity={0.04} />
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Reveal>
        <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
          <Box sx={{ width: 40, height: 3, borderRadius: 3, background: BRAND_GRAD, mx: 'auto', mb: 2.5 }} />
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2rem', md: '2.6rem' },
              fontWeight: 300,
              color: '#1A2332',
              letterSpacing: '-0.01em',
              textWrap: 'balance',
            }}
          >
            {t.contact.title}
          </Typography>
        </Box>

        <Grid container spacing={{ xs: 4, md: 6 }}>
          <Grid size={{ xs: 12, lg: 6 }}>
            <Card
              sx={{
                p: { xs: 3, md: 4 },
                height: '100%',
                borderRadius: '20px',
                background: 'linear-gradient(135deg, #F1F5FF 0%, #FAFBFF 100%)',
                border: '1px solid rgba(0, 85, 184, 0.1)',
                boxShadow: '0 14px 38px -20px rgba(0,85,184,0.18)',
              }}
            >
              <CardContent sx={{ p: 0 }}>
                <Typography
                  variant="h5"
                  sx={{
                    mb: 4,
                    fontWeight: 600,
                    color: '#1A2332',
                    fontSize: { xs: '1.25rem', md: '1.5rem' },
                  }}
                >
                  {t.contact.title}
                </Typography>

                <Grid container spacing={3}>
                  {contactInfo.map((info, index) => (
                    <Grid size={12} key={index}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 2,
                          p: 2,
                          backgroundColor: 'rgba(255, 255, 255, 0.7)',
                          borderRadius: 2,
                          backdropFilter: 'blur(10px)',
                        }}
                      >
                        <IconButton
                          component="span"
                          aria-hidden
                          tabIndex={-1}
                          disableRipple
                          sx={{
                            backgroundColor: 'rgba(0, 85, 184, 0.1)',
                            cursor: 'default',
                          }}
                        >
                          {info.icon}
                        </IconButton>
                        <Typography
                          variant="body1"
                          sx={{
                            color: '#4A5568',
                            fontWeight: 500,
                            fontSize: { xs: '0.875rem', md: '1rem' },
                            lineHeight: 1.6,
                            mt: 1,
                          }}
                        >
                          {info.title}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, lg: 6 }}>
            <Card sx={{ p: { xs: 3, md: 4 }, height: '100%', borderRadius: '20px', border: '1px solid #E6EEF9', boxShadow: '0 14px 38px -20px rgba(0,85,184,0.14)' }}>
              <CardContent sx={{ p: 0 }}>
                <Typography
                  variant="h5"
                  sx={{
                    mb: 4,
                    fontWeight: 600,
                    color: '#1A2332',
                    fontSize: { xs: '1.25rem', md: '1.5rem' },
                  }}
                >
                  {t.contact.form.send}
                </Typography>

                <Box component="form" onSubmit={handleSubmit}>
                  {status.type && (
                    <Grid size={12} sx={{ mb: 2 }}>
                      <Alert severity={status.type}>
                        {status.message}
                      </Alert>
                    </Grid>
                  )}
                  
                  <Grid container spacing={3}>
                    <Grid size={12}>
                      <TextField
                        fullWidth
                        name="name"
                        label={t.contact.form.name}
                        value={formData.name}
                        onChange={handleChange}
                        required
                        disabled={isLoading}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            fontSize: { xs: '0.875rem', md: '1rem' },
                          },
                        }}
                      />
                    </Grid>
                    <Grid size={12}>
                      <TextField
                        fullWidth
                        name="email"
                        label={t.contact.form.email}
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        disabled={isLoading}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            fontSize: { xs: '0.875rem', md: '1rem' },
                          },
                        }}
                      />
                    </Grid>
                    <Grid size={12}>
                      <TextField
                        fullWidth
                        name="message"
                        label={t.contact.form.message}
                        multiline
                        rows={4}
                        value={formData.message}
                        onChange={handleChange}
                        required
                        disabled={isLoading}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            fontSize: { xs: '0.875rem', md: '1rem' },
                          },
                        }}
                      />
                    </Grid>
                    <Grid size={12}>
                      <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        size="large"
                        disabled={isLoading}
                        sx={{
                          py: { xs: 1.5, md: 2 },
                          fontSize: { xs: '1rem', md: '1.125rem' },
                          fontWeight: 600,
                        }}
                      >
                        {isLoading ? (
                          <>
                            <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                            {t.contact.form.sending}
                          </>
                        ) : (
                          t.contact.form.send
                        )}
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        </Reveal>
      </Container>
    </Box>
  );
};

export default ContactSection;